import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import harnessImage from "../assets/images/harness.png";
import { trackEvent } from "../utils/analytics";
import { useVehicle } from "../context/VehicleContext";

/* ── Mini Gallery ─────────────────────────────────────────────────── */
function MobileGallery({ images = [] }) {
  const [active, setActive] = useState(0);
  const galleryImages = images.length > 0 ? images : [harnessImage];

  return (
    <div style={{ width: "100%", background: "#f6f7f9", borderRadius: 24, overflow: "hidden", marginBottom: 20 }}>
    <div style={{ width: "100%", aspectRatio: "1", display: "flex", alignItems: "center", justifyContent: "center" }}>
  <img src={galleryImages[active]} alt="product" style={{ width: "80%", height: "80%", objectFit: "contain" }} />
</div>

      {galleryImages.length > 1 && (
        <div style={{ display: "flex", gap: 8, padding: "0 16px 16px", overflowX: "auto", scrollbarWidth: "none" }}>
          {galleryImages.map((img, i) => (
            <div key={i} onClick={() => setActive(i)} style={{
              flexShrink: 0, width: 56, height: 56, borderRadius: 12,
              background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
              border: active === i ? "2px solid #4A8CFF" : "2px solid transparent",
              cursor: "pointer", overflow: "hidden"
            }}>
              <img src={img} alt="" style={{ width: "80%", height: "80%", objectFit: "contain" }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main Screen ──────────────────────────────────────────────────── */
export default function ProductScreenMobile() {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [quantity, setQuantity] = useState(1);
const [cartSuccess, setCartSuccess] = useState(false);
const [cartError, setCartError] = useState("");
const [crossSell, setCrossSell] = useState([]);
const [zip, setZip] = useState(() => localStorage.getItem("deliveryZip") || "");
const [zipInput, setZipInput] = useState("");
const [showZipInput, setShowZipInput] = useState(false);

const { selectedVehicle } = useVehicle();

const usesVariants = useMemo(() => product?.variants?.length > 0, [product]);

const selectedVariant = useMemo(() =>
  usesVariants ? product?.variants?.find(v => v.condition === selectedCondition) : null,
  [product, selectedCondition, usesVariants]
);

const outOfStock = !selectedVariant || selectedVariant.stock <= 0;

const today = new Date();
const startDelivery = new Date(today); startDelivery.setDate(today.getDate() + 3);
const endDelivery = new Date(today); endDelivery.setDate(today.getDate() + 4);
const deliveryStart = startDelivery.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const deliveryEnd = endDelivery.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const fallbackDelivery = `${deliveryStart} – ${deliveryEnd}`;


  const price = selectedVariant?.price ?? product?.price ?? 0;
  const dollars = Math.floor(price);
  const cents = (price % 1).toFixed(2).substring(2);
  const cheapestPrice = usesVariants ? Math.min(...(product?.variants || []).map(v => v.price)) : price;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await fetch(`https://eleven11-i6v4.onrender.com/api/listings/${listingId}`);
        if (!res.ok) throw new Error("Not found");
        const data = await res.json();
        setProduct(data);
        if (data.variants?.length > 0) {
          const cheapest = [...data.variants].sort((a, b) => a.price - b.price)[0];
          setSelectedCondition(cheapest.condition);
        }
        trackEvent("product_view", { listingId: data.listingId, listingTitle: data.title });
      } catch { setProduct(false); }
    };
    fetchProduct();
  }, [listingId]);

  useEffect(() => {
    let url = "https://eleven11-i6v4.onrender.com/api/listings";
    if (selectedVehicle) {
      const params = new URLSearchParams({
        year: selectedVehicle.year,
        manufacturer: selectedVehicle.manufacturer,
        model: selectedVehicle.model
      });
      url += `?${params.toString()}`;
    }
    fetch(url).then(r => r.json()).then(data => {
      const CROSS_SELL_CATEGORIES = ["Fluids & Maintenance", "Lubricants & Sprays", "Marine Detailing"];
      const others = data.filter(l =>
        l.listingId !== listingId && l.images?.length > 0 && CROSS_SELL_CATEGORIES.includes(l.category)
      );
      setCrossSell([...others].sort(() => Math.random() - 0.5).slice(0, 6));
    }).catch(() => {});
  }, [listingId, selectedVehicle]);

  const handleOrderNow = async () => {
    if (!product?.listingId) return;
    const finalPrice = selectedVariant?.price ?? product?.price;
    if (!finalPrice || isNaN(finalPrice)) { alert("Invalid price"); return; }
    try {
      const cartRes = await fetch("https://eleven11-i6v4.onrender.com/api/cart");
      const cartData = await cartRes.json();
      const alreadyInCart = (cartData.items || []).some(
        item => item.listingId === product.listingId && item.selectedCondition === selectedCondition
      );
      if (!alreadyInCart) {
        await fetch("https://eleven11-i6v4.onrender.com/api/cart", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ productId: product.listingId, qty: quantity, selectedCondition, selectedVariant, price: finalPrice })
        });
      }
      navigate("/checkout", {
        state: { product, quantity, selectedCondition, selectedVariant, price: finalPrice, listingId: product?.listingId, title: product?.title }
      });
    } catch (err) { console.error(err); }
  };

const handleAddToCart = async () => {
    if (!product?.listingId || outOfStock) return;
    const finalPrice = selectedVariant?.price ?? product?.price;
    if (!finalPrice || isNaN(finalPrice)) return;
    try {
      const res = await fetch("https://eleven11-i6v4.onrender.com/api/cart", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId: product.listingId, qty: quantity, selectedCondition, selectedVariant, price: finalPrice })
      });
      const data = await res.json();
      if (!res.ok) {
        setCartError(data.error || "Could not add to cart");
        setTimeout(() => setCartError(""), 3000);
        return;
      }
      trackEvent("add_to_cart", { listingId: product.listingId, listingTitle: product.title });
      setCartSuccess(true);
      setTimeout(() => setCartSuccess(false), 2000);
    } catch (err) {
      console.error(err);
      setCartError("Could not add to cart");
      setTimeout(() => setCartError(""), 3000);
    }
  };
  if (product === false) return <div style={{ padding: 24, fontFamily: "system-ui" }}>Listing not found.</div>;
  if (!product) return <div style={{ padding: 24, fontFamily: "system-ui" }}>Loading...</div>;

  return (
    <div style={{ background: "#F6F7FB", minHeight: "100vh", fontFamily: "-apple-system, BlinkMacSystemFont, system-ui", paddingBottom: 100 }}>

      {/* Gallery */}
      <div style={{ padding: "16px 16px 0" }}>
      <MobileGallery images={(selectedVariant?.images?.length > 0 ? selectedVariant.images : product.images) || []} />
      </div>

      <div style={{ padding: "0 16px" }}>

        {/* Title & Badge */}
    <p style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", letterSpacing: 1.4, margin: "0 0 6px", textTransform: "uppercase" }}>
         {(product?.partType || "FACTORY OEM").toUpperCase()}
        </p>
        <h1 style={{ fontSize: 22, fontWeight: 600, lineHeight: "30px", letterSpacing: "-0.4px", color: "#1d1d1f", margin: "0 0 10px" }}>
          {product.title}
        </h1>

        {product.badge && (
          <span style={{ fontSize: 12, fontWeight: 600, color: "#3a3a3c", background: "linear-gradient(135deg,#f5f5f7,#e8e8ed)", padding: "4px 12px", borderRadius: 999, border: "1px solid rgba(0,0,0,0.08)", display: "inline-block", marginBottom: 12 }}>
            {product.badge}
          </span>
        )}

        <div style={{ height: 1, background: "rgba(0,0,0,0.08)", margin: "14px 0" }} />

        {/* Price */}
        <div style={{ display: "inline-block", border: "1px solid #ddd", padding: "4px 10px", borderRadius: 4, fontSize: 12, marginBottom: 8 }}>
          Eleven Market Value
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 8 }}>
          <span style={{ fontSize: 16, marginTop: 4 }}>$</span>
          <span style={{ fontSize: 38, fontWeight: 600, letterSpacing: "-1px", lineHeight: 1 }}>{dollars}</span>
          <span style={{ fontSize: 16, marginTop: 4 }}>{cents}</span>
        </div>



        <p style={{ fontSize: 13, color: "#1d1d1f", margin: "0 0 4px" }}>
          Get <b>Fast, Free Shipping</b> with 1111
        </p>
{(product?.manufacturer || product?.compatibleModels?.length > 0 || product?.model) && (
  <p style={{ fontSize: 13, color: "#007185", margin: "0 0 16px", fontWeight: 600 }}>
    DIRECT FIT —{" "}
    {product?.compatibleModels?.length > 0
      ? product.compatibleModels.slice(0, 3).join(" · ")
      : [
          product.yearStart && product.yearEnd && product.yearStart !== product.yearEnd
            ? `${product.yearStart}–${product.yearEnd}`
            : product.yearStart || product.yearEnd || "",
          product.manufacturer || "",
          product.model || ""
        ].filter(Boolean).join(" ")
    }
  </p>
)}



        {/* Condition Variants */}
        {usesVariants && (
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 14, fontWeight: 600, margin: "0 0 10px" }}>
              Condition: <span style={{ fontWeight: 400 }}>{selectedVariant?.label || "Select"}</span>
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              {product.variants.map(variant => (
                <div key={variant.condition} onClick={() => setSelectedCondition(variant.condition)} style={{
                  flex: 1, border: selectedCondition === variant.condition ? "2px solid #00A6FF" : "1px solid #e5e5e5",
                  borderRadius: 14, padding: 10, cursor: "pointer", background: "#fff",
                  opacity: variant.stock === 0 ? 0.5 : 1,
                  pointerEvents: variant.stock === 0 ? "none" : "auto",
                  position: "relative", transition: "all 0.2s"
                }}>
                  {variant.price === cheapestPrice && (
                    <div style={{ position: "absolute", top: -8, left: 8, fontSize: 9, fontWeight: 800, background: "#0a84ff", color: "#fff", padding: "2px 8px", borderRadius: 999 }}>
                      BEST VALUE
                    </div>
                  )}
               <img src={variant.images?.[0] || product.images?.[0] || harnessImage} alt="" style={{ width: "100%", height: 80, objectFit: "contain" }} />
                  <p style={{ fontSize: 12, fontWeight: 600, margin: "6px 0 2px" }}>{variant.label}</p>
             <p style={{ fontSize: 11, color: "#86868B", margin: "0 0 4px" }}>
  {variant.condition === "new" ? "OEM • Factory Fresh" : "OEM Like New • Fully Tested"}
</p>
<p style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>${variant.price.toFixed(2)}</p>
{variant.condition === "new" && (
  <p style={{ fontSize: 10, color: "#9ca3af", margin: "3px 0 0", fontWeight: 500 }}>
    Dealer Price
  </p>
)}
                  {variant.stock > 0 && variant.stock <= 3 && (
                    <p style={{ fontSize: 10, color: "#d93025", margin: "4px 0 0" }}>Only {variant.stock} left</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Delivery */}
        <div style={{ background: "#fff", borderRadius: 16, padding: 14, marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
   <div style={{ fontSize: 13, marginBottom: 6 }}>
  <span style={{ fontWeight: 500 }}>FREE delivery</span>
  <span style={{ fontWeight: 600, marginLeft: 8 }}>{product.delivery || fallbackDelivery}</span>
</div>
<div style={{ fontSize: 12, color: "#565959", marginBottom: 6 }}>⚡ Fastest delivery <b>{product.fastest || deliveryStart}</b></div>
          <div style={{ fontSize: 12, color: "#565959" }}>
            Delivering to <span style={{ fontWeight: 500 }}>{zip || "your location"}</span>{" "}
            <span style={{ color: "#007185", cursor: "pointer" }} onClick={() => { setZipInput(zip); setShowZipInput(v => !v); }}>Update</span>
          </div>
          {showZipInput && (
            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <input
                value={zipInput}
                onChange={e => setZipInput(e.target.value)}
                placeholder="Enter ZIP code"
                maxLength={5}
                style={{ flex: 1, padding: "8px 12px", borderRadius: 10, border: "1px solid #ddd", fontSize: 16, outline: "none" }}
                onKeyDown={e => {
                  if (e.key === "Enter" && zipInput.length === 5) {
                    setZip(zipInput);
                    localStorage.setItem("deliveryZip", zipInput);
                    setShowZipInput(false);
                  }
                }}
              />
              <button
                onClick={() => {
                  if (zipInput.length === 5) {
                    setZip(zipInput);
                    localStorage.setItem("deliveryZip", zipInput);
                    setShowZipInput(false);
                  }
                }}
                style={{ padding: "8px 14px", borderRadius: 10, border: "none", background: "#0f0f17", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
              >
                Save
              </button>
            </div>
          )}

        </div>

        {/* Stock */}
        <p style={{ fontSize: 16, color: "#05b4ff", fontWeight: 600, margin: "0 0 12px" }}>
          {selectedVariant?.stock > 0 ? "In Stock" : "Out of Stock"}
        </p>

        {/* Quantity */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 13, color: "#6E7385", fontWeight: 500 }}>Quantity</span>
          <div style={{ display: "flex", alignItems: "center", background: "#F5F7FB", borderRadius: 14, border: "1px solid #E4E8F0", padding: 4, gap: 4 }}>
            <button onClick={() => quantity > 1 && setQuantity(quantity - 1)} style={{ width: 32, height: 32, borderRadius: 10, border: "none", background: "#fff", fontSize: 18, cursor: "pointer", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}>−</button>
            <span style={{ fontSize: 17, fontWeight: 600, width: 32, textAlign: "center" }}>{quantity}</span>
         <button
  onClick={() => quantity < (selectedVariant?.stock || 1) && setQuantity(quantity + 1)}
  style={{ width: 32, height: 32, borderRadius: 10, border: "none", background: "#fff", fontSize: 18, cursor: quantity >= (selectedVariant?.stock || 1) ? "not-allowed" : "pointer", opacity: quantity >= (selectedVariant?.stock || 1) ? 0.4 : 1, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}
>+</button>
          </div>
        </div>

     {/* Cart feedback */}
        {cartError && (
          <div style={{ background: "#FEF2F2", color: "#DC2626", fontSize: 13, fontWeight: 700, padding: "10px 14px", borderRadius: 12, marginBottom: 10, textAlign: "center" }}>
            ⚠ {cartError}
          </div>
        )}
        {cartSuccess && (
          <div style={{ background: "#EDFDF4", color: "#15803D", fontSize: 13, fontWeight: 700, padding: "10px 14px", borderRadius: 12, marginBottom: 10, textAlign: "center" }}>
            ✓ Added to cart
          </div>
        )}

        {/* Buttons */}
        <button
          onClick={outOfStock ? undefined : handleOrderNow}
          disabled={outOfStock}
          style={{ width: "100%", padding: "14px", borderRadius: 25, border: "none", background: outOfStock ? "#ccc" : "linear-gradient(180deg,#5a8efc,#1f57c8)", color: "#fff", fontWeight: 600, fontSize: 16, cursor: outOfStock ? "not-allowed" : "pointer", marginBottom: 10, opacity: outOfStock ? 0.5 : 1, boxShadow: outOfStock ? "none" : "0 10px 20px rgba(62,111,227,0.35)" }}>
          Order Now
        </button>
        <button
          onClick={outOfStock ? undefined : handleAddToCart}
          disabled={outOfStock}
          style={{ width: "100%", padding: "14px", borderRadius: 25, border: "none", background: outOfStock ? "#ccc" : "linear-gradient(180deg,#5a8efc,#1f57c8)", color: "#fff", fontWeight: 600, fontSize: 16, cursor: outOfStock ? "not-allowed" : "pointer", marginBottom: 10, opacity: outOfStock ? 0.5 : 1, boxShadow: outOfStock ? "none" : "0 10px 20px rgba(62,111,227,0.35)" }}>
          Add to Cart
        </button>



<button disabled style={{ width: "100%", padding: "14px", borderRadius: 25, border: "1px solid #E6EAF0", background: "#fff", color: "#aaa", fontWeight: 700, fontSize: 16, cursor: "not-allowed", opacity: 0.6 }}>
          Affirm
        </button>
        <p style={{ fontSize: 12, color: "#6B7280", textAlign: "center", margin: "8px 0 0" }}>Buy Now Pay Over Time — Coming Soon</p>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "24px 0 16px" }}>
          <div style={{ flex: 1, height: 1, background: "#D8DCE3" }} />
          <span style={{ fontSize: 14, letterSpacing: 2, color: "#9AA1B2", fontWeight: 500 }}>ELEVEN</span>
          <div style={{ flex: 1, height: 1, background: "#D8DCE3" }} />
        </div>

        {/* Local Pickup */}
        <div style={{ background: "#f9f9fb", borderRadius: 16, padding: "14px 16px", marginBottom: 24, border: "1px solid #eee" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#2F3441" }}>Local Pickup · FACILITY ELEVEN</span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: "#EDEDF0", color: "#9CA3AF", letterSpacing: 0.8 }}>COMING SOON</span>
          </div>
          <p style={{ fontSize: 12, color: "#B0B7C3", margin: 0, lineHeight: "17px" }}>
            Pick up at FACILITY ELEVEN or an Authorized Dealer near you.
          </p>
        </div>

        {/* Specs */}
        <div style={{ background: "#fff", borderRadius: 16, padding: 16, marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
          <p style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: "#9ca3af", textTransform: "uppercase", margin: "0 0 14px" }}>Specifications</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
     { label: "Model", value: product.model || "—" },
              { label: "EID", value: product.eid || "—" },
              { label: "Color", value: product.color || "—" },
              { label: "Weight", value: product.weightValue ? `${product.weightValue} ${product.weightUnit || ""}` : "—" },
              { label: "OEM Part #", value: product.partNumber || "—" },
              { label: "Manufacturer", value: product.manufacturer || product.brand || "—" },
            ].map(spec => (
              <div key={spec.label}>
                <p style={{ fontSize: 11, color: "#86868B", fontWeight: 500, margin: "0 0 4px", letterSpacing: "0.02em" }}>{spec.label}</p>
                <p style={{ fontSize: 14, fontWeight: 600, color: "#1d1d1f", margin: 0 }}>{spec.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Cross Sell */}
        {crossSell.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, color: "#9ca3af", textTransform: "uppercase", margin: "0 0 4px" }}>CUSTOMERS ALSO BOUGHT</p>
            <h2 style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-0.5px", color: "#0f0f17", margin: "0 0 14px" }}>Complete Your Setup</h2>
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6, scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
              {crossSell.map(item => {
                const variant = item.variants?.[0];
                const itemPrice = variant?.price ?? item.price ?? 0;
                const condition = variant?.condition ?? item.condition ?? "used";
                return (
                  <div key={item._id} style={{ flexShrink: 0, width: 160, background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                    <div onClick={() => navigate(`/product/${item.listingId}`)} style={{ cursor: "pointer" }}>
                      <div style={{ width: "100%", aspectRatio: "1", background: "#f9fafb", overflow: "hidden" }}>
                        <img src={item.images[0]} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </div>
                      <div style={{ padding: "10px 12px 8px" }}>
                        <p style={{ fontSize: 9, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1, margin: "0 0 3px" }}>{item.category}</p>
                        <p style={{ fontSize: 12, fontWeight: 600, color: "#0f0f17", margin: "0 0 4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</p>
                        <p style={{ fontSize: 14, fontWeight: 800, color: "#0f0f17", margin: 0 }}>${Number(itemPrice).toFixed(2)}</p>
                      </div>
                    </div>
                    <div style={{ padding: "0 12px 12px" }}>
                      <button onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await fetch("https://eleven11-i6v4.onrender.com/api/cart", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ productId: item.listingId, qty: 1, selectedCondition: condition, selectedVariant: variant || null, price: Number(itemPrice) })
                          });
                          trackEvent("add_to_cart", { listingId: item.listingId, listingTitle: item.title });
                          e.target.innerText = "✓ Added";
                          e.target.style.background = "#EDFDF4";
                          e.target.style.color = "#15803D";
                          setTimeout(() => {
                            e.target.innerText = "Add";
                            e.target.style.background = "#0f0f17";
                            e.target.style.color = "#fff";
                          }, 2000);
                        } catch {}
                      }} style={{ width: "100%", padding: "8px 0", border: "none", borderRadius: 10, background: "#0f0f17", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        Add
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}